import { useEffect, useState } from 'react';
import Button from '@/components/ui/Button';
import { useAuthContext } from '@/contexts/AuthContext';
import { getMyDeletionStatus, keepMyAccount } from '@/core/apiProxy';
import { PENDING_DELETION_EVENT, type PendingDeletionDetail } from '@/core/pendingDeletion';
import { t } from '../../i18n/t';
import i18n from '../../i18n/config';

/**
 * Round 139 (owner ruling 2026-10-24: "The learner can cancel the deletion themselves by signing in if they are the ones who deleted the
 * account. If the account was deleted by the super admin, only he can undo that.")
 *
 * A learner whose account is scheduled for deletion signs in and sees this screen instead of the app: the date, "Keep my account" and
 * "Sign out". It appears when the profile carries the server-set `deletion` flag, or when any API answered ACCOUNT_PENDING_DELETION
 * (core/pendingDeletion.ts). The server decides whether the learner may keep the account (`canCancel`): a super admin's deletion shows
 * only the "closed" sentence and Sign out. Mounted once inside AuthProvider, learner host only.
 */
const formatDate = (iso: string | null) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return iso; }
};

export default function PendingDeletionScreen() {
  const { user, profile, logout, refreshProfile } = useAuthContext();
  const [info, setInfo] = useState<PendingDeletionDetail | null>(null);
  const [stage, setStage] = useState<'ask' | 'keeping' | 'kept'>('ask');
  const [error, setError] = useState('');

  // any learner API that answered ACCOUNT_PENDING_DELETION
  useEffect(() => {
    const on = (e: Event) => { setInfo((e as CustomEvent<PendingDeletionDetail>).detail); setStage('ask'); };
    window.addEventListener(PENDING_DELETION_EVENT, on);
    return () => window.removeEventListener(PENDING_DELETION_EVENT, on);
  }, []);

  // the server-set flag on the profile (read at sign-in): ask the server for the authoritative state
  const flagged = !!(profile as { deletion?: unknown } | null)?.deletion;
  useEffect(() => {
    if (!user || !flagged) return;
    let alive = true;
    Promise.resolve()
      .then(() => getMyDeletionStatus())
      .then((s) => { if (alive && (s.status === 'pending' || s.status === 'purging')) setInfo({ purgeAfter: s.purgeAfter, canCancel: s.canCancel }); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [user?.uid, flagged]);

  useEffect(() => { if (!user) { setInfo(null); setStage('ask'); setError(''); } }, [user]);

  if (!user || !info) return null;
  const date = formatDate(info.purgeAfter);

  async function keep() {
    setStage('keeping');
    setError('');
    try {
      await keepMyAccount();
      await refreshProfile();
      setStage('kept');
    } catch (e: unknown) {
      const code = (e as { code?: unknown })?.code;
      setError(code === 'GRACE_PERIOD_OVER' ? t('account:pendingDeletion.graceOver') : code === 'ADMIN_DELETION' ? t('account:pendingDeletion.closed') : t('account:pendingDeletion.error'));
      setStage('ask');
    }
  }

  return (
    <div className="fixed inset-0 z-[9998] bg-paper overflow-y-auto flex items-center justify-center px-4 py-8"
      role="dialog" aria-modal="true" aria-labelledby="pending-deletion-title" data-testid="pending-deletion-screen">
      <div className="w-full max-w-md bg-paper-2 border border-line rounded-xl p-6 space-y-4">
        {stage === 'kept' ? (
          <>
            <h1 id="pending-deletion-title" className="text-heading-md text-ink">{t('account:pendingDeletion.kept')}</h1>
            <p className="text-body-md text-subtle">{t('account:pendingDeletion.keepExplained')}</p>
            <Button fullWidth onClick={() => { setInfo(null); window.location.assign('/'); }}>{t('account:pendingDeletion.continue')}</Button>
          </>
        ) : info.canCancel ? (
          <>
            <h1 id="pending-deletion-title" className="text-heading-md text-ink">
              {date ? t('account:pendingDeletion.title', { date }) : t('account:pendingDeletion.titleNoDate')}
            </h1>
            <p className="text-body-md text-subtle">{t('account:pendingDeletion.keepExplained')}</p>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <div className="flex flex-wrap gap-3">
              <Button className="flex-auto min-w-fit" onClick={keep} disabled={stage === 'keeping'}>
                {stage === 'keeping' ? t('account:pendingDeletion.keeping') : t('account:pendingDeletion.keep')}
              </Button>
              <Button className="flex-auto min-w-fit" variant="secondary" onClick={() => { void logout(); }} disabled={stage === 'keeping'}>
                {t('account:pendingDeletion.signOut')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1 id="pending-deletion-title" className="text-heading-md text-ink">{t('account:pendingDeletion.closed')}</h1>
            <Button fullWidth variant="secondary" onClick={() => { void logout(); }}>{t('account:pendingDeletion.signOut')}</Button>
          </>
        )}
      </div>
    </div>
  );
}
